/**
 * 수정제안 모달 — 필드 우선 방식 (M15 / A11, 재설계).
 *
 * 좌: AnnotationCanvas. 우: 자연어 피드백 + 현재 필드 값 목록.
 *   - 우측 목록에서 각 필드의 현재 값(빈 값 강조) 확인 → "위치 표시" 로 그리기 시작.
 *   - 제출 시 onSubmitted(fileId, requestId) 통지 후 모달 닫음 (행에 진행 표시).
 */

import { Lightbulb, Loader2, Plus, Sparkles, Trash2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import type { InvoiceFileOut } from "@/lib/api/jobs";
import { cn } from "@/lib/cn";

import { localizeAiText } from "../aiText";
import { EDITABLE_FIELDS } from "../fieldLabels";
import AnnotationCanvas from "./AnnotationCanvas";
import { useAnnotationDrawing } from "./useAnnotationDrawing";
import { useCreateCorrection, useSuggestedQuestions } from "./useCorrections";

interface Props {
  jobId: string;
  file: InvoiceFileOut;
  onClose: () => void;
  /** 제출 성공 시 (fileId, correctionRequestId) 통지 → 행 진행 표시용. */
  onSubmitted?: (fileId: string, requestId: string) => void;
  /**
   * 최대화 여부. EditRecordModal 과 동일 크기 로직 공유용.
   * 미지정 시 편집창 저장값(``editRecordModal:maximized``, 기본 ON)을 따른다.
   */
  maximized?: boolean;
}

/** 편집창 최대화 선호값 저장 키 (EditRecordModal 과 동일). */
const MAXIMIZE_STORAGE_KEY = "editRecordModal:maximized";

function toStr(v: unknown): string {
  if (v == null) return "";
  return String(v);
}

export default function CorrectionSuggestionModal({
  jobId,
  file,
  onClose,
  onSubmitted,
  maximized,
}: Props) {
  const { t, i18n } = useTranslation();
  const drawing = useAnnotationDrawing();
  // 편집창과 동일한 최대화 크기를 공유. prop 우선, 없으면 저장값(기본 ON).
  const maxed = useMemo(() => {
    if (typeof maximized === "boolean") return maximized;
    try {
      return localStorage.getItem(MAXIMIZE_STORAGE_KEY) !== "0";
    } catch {
      return true;
    }
  }, [maximized]);

  // 1차 검수 완료 AI 검토에서 needs_review 로 나온 지적사항 목록.
  const aiIssues = useMemo<string[]>(() => {
    const r = file.record?.ai_review;
    if (!r || r.verdict !== "needs_review") return [];
    return (r.issues ?? [])
      .map((s) => localizeAiText(String(s).trim(), i18n.language))
      .filter((s) => s.length > 0);
  }, [file.record?.ai_review, i18n.language]);

  // AI 지적사항이 있으면 피드백란에 자동 프리필(헤더 + 불릿). 이후 편집/삭제 가능.
  const [feedback, setFeedback] = useState<string>(() => {
    if (aiIssues.length === 0) return "";
    return `${t("corrections.modal.aiIssuesHeader")}\n${aiIssues.map((s) => `- ${s}`).join("\n")}`;
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const createMutation = useCreateCorrection();

  // #5 — 추천 질문 (검수 의심사항 기반, kta-chat-vl 생성).
  const suggestMutation = useSuggestedQuestions();
  const [questions, setQuestions] = useState<string[]>([]);
  const [suggestTried, setSuggestTried] = useState(false);

  const handleSuggest = useCallback(async () => {
    setApiError(null);
    setSuggestTried(true);
    try {
      const res = await suggestMutation.mutateAsync({ jobId, fileId: file.id });
      setQuestions(res.questions);
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : (err as Error).message;
      setApiError(msg);
    }
  }, [suggestMutation, jobId, file.id]);

  const insertQuestion = useCallback((q: string) => {
    // 피드백 입력창에 줄바꿈으로 이어붙인다 (기존 내용 보존).
    setFeedback((prev) => (prev.trim() === "" ? q : `${prev.trim()}\n${q}`));
  }, []);

  const csv = (file.record?.csv_data ?? {}) as Record<string, unknown>;
  const groupByField = new Map(drawing.groups.map((g) => [g.field, g]));

  // Esc: 그리기 중이면 취소, 아니면 닫기.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (drawing.activeField !== null) drawing.cancelField();
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawing, onClose]);

  const canSubmit = feedback.trim().length > 0 && !createMutation.isPending;

  const handleSubmit = useCallback(async () => {
    if (!canSubmit) return;
    setApiError(null);
    try {
      const res = await createMutation.mutateAsync({
        jobId,
        fileId: file.id,
        payload: {
          feedback: feedback.trim(),
          annotations: drawing.groups.map((g) => ({
            id: g.id,
            field: g.field,
            page: g.page,
            label_bbox: g.labelBbox,
            value_bbox: g.valueBbox,
          })),
        },
      });
      onSubmitted?.(file.id, res.id);
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : (err as Error).message;
      setApiError(msg);
    }
  }, [canSubmit, createMutation, jobId, file.id, feedback, drawing.groups, onSubmitted, onClose]);

  // Enter: 재추출 실행(제출). 피드백 textarea 에서는 Shift+Enter 로 줄바꿈.
  //  - 그리기 중이거나 제출 불가(canSubmit=false)면 발동하지 않는다.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
      if (drawing.activeField !== null || !canSubmit) return;
      e.preventDefault();
      void handleSubmit();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [drawing.activeField, canSubmit, handleSubmit]);

  return (
    <div
      className={cn(
        "fixed inset-0 z-[60] flex items-center justify-center bg-black/40",
        maxed ? "p-2" : "p-4",
      )}
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div
        className={cn(
          "relative flex w-full flex-col overflow-hidden rounded-lg bg-white shadow-xl",
          maxed ? "h-[calc(100vh-1rem)] max-w-none" : "h-[90vh] w-[95vw] max-w-6xl",
        )}
      >
        {/* 그리기 중 — 표시 영역(좌측 PDF) 외 전체 암전 (집중 유도) */}
        {drawing.activeField !== null && (
          <div className="pointer-events-none absolute inset-0 z-30 rounded-lg bg-black/60" />
        )}
        {/* Header */}
        <header className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">{t("corrections.modal.title")}</h2>
            <p className="mt-0.5 text-xs text-slate-500">{file.original_name}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
            aria-label={t("common.actions.close")}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        {/* Body */}
        <div className="flex min-h-0 flex-1">
          {/* 좌: 캔버스 — 그리기 중엔 암전 위로 올려 밝게 유지 */}
          <div
            className={cn(
              "flex min-w-0 flex-[3] flex-col border-r border-slate-200",
              drawing.activeField !== null && "relative z-40 bg-white",
            )}
          >
            <AnnotationCanvas jobId={jobId} fileId={file.id} drawing={drawing} />
          </div>

          {/* 우: 폼 — 상단 피드백(고정) + 하단 필드목록(독립 스크롤) */}
          <div className="flex min-h-0 w-96 flex-col p-4">
            {/* ① 자연어 피드백 (고정, 충분한 높이) */}
            <label className="shrink-0 text-xs font-medium text-slate-600">
              {t("corrections.modal.feedbackLabel")}
              <span className="text-red-500"> *</span>
            </label>
            {aiIssues.length > 0 && (
              <p className="mt-1 flex items-start gap-1 text-[11px] text-amber-700">
                <Lightbulb className="mt-0.5 h-3 w-3 flex-shrink-0" aria-hidden />
                <span>{t("corrections.modal.aiIssuesPrefilled", { count: aiIssues.length })}</span>
              </p>
            )}
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t("corrections.modal.feedbackPlaceholder")}
              className="mt-1 h-40 w-full shrink-0 resize-none rounded-md border border-slate-200 px-2 py-1.5 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-200"
            />

            {/* #5 — 추천 질문: 의심사항 기반 자동 생성 → 클릭 시 피드백에 삽입 */}
            <div className="mt-2 shrink-0">
              <button
                type="button"
                onClick={handleSuggest}
                disabled={suggestMutation.isPending}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-xs font-medium transition",
                  suggestMutation.isPending
                    ? "cursor-wait border-slate-200 text-slate-400"
                    : "border-amber-300 text-amber-700 hover:bg-amber-50",
                )}
              >
                {suggestMutation.isPending ? (
                  <Loader2 className="h-3 w-3 animate-spin" />
                ) : (
                  <Lightbulb className="h-3 w-3" />
                )}
                {t("corrections.modal.suggest.button")}
              </button>

              {questions.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {questions.map((q, i) => (
                    <li key={`q-${i}`}>
                      <button
                        type="button"
                        onClick={() => insertQuestion(q)}
                        title={t("corrections.modal.suggest.insertHint")}
                        className="flex w-full items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5 text-left text-[11px] text-amber-900 transition hover:bg-amber-100"
                      >
                        <Plus className="mt-0.5 h-3 w-3 flex-shrink-0 text-amber-600" />
                        <span>{q}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {suggestTried &&
                !suggestMutation.isPending &&
                questions.length === 0 &&
                apiError === null && (
                  <p className="mt-2 text-[11px] text-slate-400">
                    {t("corrections.modal.suggest.empty")}
                  </p>
                )}
            </div>

            {/* ② 현재 필드 값 + 위치 표시 (남은 공간 차지 + 내부 스크롤) */}
            <div className="mt-4 flex min-h-0 flex-1 flex-col">
              <h3 className="shrink-0 text-xs font-medium text-slate-600">
                {t("corrections.modal.fieldsLabel")}
              </h3>
              <p className="mt-0.5 shrink-0 text-[11px] text-slate-400">
                {t("corrections.modal.fieldsHint")}
              </p>
              <ul className="mt-2 min-h-0 flex-1 space-y-1 overflow-y-auto pr-1">
                {EDITABLE_FIELDS.map((meta) => {
                  const value = toStr(csv[meta.key]);
                  const empty = value.trim() === "";
                  const group = groupByField.get(meta.key);
                  const isActive = drawing.activeField === meta.key;
                  return (
                    <li
                      key={meta.key}
                      className={cn(
                        "rounded-md border px-2 py-1.5 text-xs",
                        isActive
                          ? "border-blue-400 bg-blue-50"
                          : group
                            ? "border-red-200 bg-red-50/40"
                            : "border-slate-200",
                      )}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="min-w-0 truncate font-medium text-slate-700">
                          {t(meta.labelI18nKey)}
                        </span>
                        <div className="flex shrink-0 items-center gap-1">
                          {group ? (
                            <>
                              <span className="rounded bg-red-500 px-1.5 py-0.5 text-[10px] text-white">
                                p{group.page}
                                {group.labelBbox ? "" : ` · ${t("corrections.modal.noLabel")}`}
                              </span>
                              <button
                                type="button"
                                onClick={() => drawing.startField(meta.key)}
                                className="rounded border border-slate-300 px-1.5 py-0.5 text-[10px] text-slate-600 hover:bg-slate-100"
                              >
                                {t("corrections.modal.redraw")}
                              </button>
                              <button
                                type="button"
                                onClick={() => drawing.removeGroup(group.id)}
                                className="rounded p-0.5 text-slate-400 hover:bg-red-100 hover:text-red-600"
                                aria-label={t("common.actions.delete")}
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              onClick={() => drawing.startField(meta.key)}
                              disabled={isActive}
                              className={cn(
                                "rounded border px-1.5 py-0.5 text-[10px] transition",
                                isActive
                                  ? "border-blue-300 text-blue-400"
                                  : "border-violet-300 text-violet-700 hover:bg-violet-50",
                              )}
                            >
                              {t("corrections.modal.markLocation")}
                            </button>
                          )}
                        </div>
                      </div>
                      {/* 현재 값 */}
                      <div
                        className={cn(
                          "mt-0.5 truncate",
                          empty ? "italic text-amber-600" : "text-slate-500",
                        )}
                        title={value}
                      >
                        {empty ? t("corrections.modal.emptyValue") : value}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>

            {apiError && (
              <div className="mt-3 shrink-0 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
                {apiError}
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <footer className="flex items-center justify-end gap-2 border-t border-slate-200 bg-slate-50 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 transition hover:bg-slate-100"
          >
            {t("common.actions.cancel")}
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white transition",
              canSubmit ? "bg-blue-600 hover:bg-blue-700" : "cursor-not-allowed bg-slate-300",
            )}
          >
            {createMutation.isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <Sparkles className="h-3 w-3" />
            )}
            {t("corrections.modal.submit")}
            <kbd className="ml-0.5 rounded border border-white/40 px-1 py-0.5 text-[10px] font-normal opacity-80">
              Enter
            </kbd>
          </button>
        </footer>
      </div>
    </div>
  );
}
